/**
 * Client-side image compression utility
 * Compresses and converts images to WebP before upload
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  skipBelowSize?: number; // Skip compression for files smaller than this (in bytes)
}

export const COMPRESSION_PRESETS = {
  checklist: { maxWidth: 1920, maxHeight: 1920, quality: 0.8, skipBelowSize: 100 * 1024 },
  avatar: { maxWidth: 512, maxHeight: 512, quality: 0.85, skipBelowSize: 50 * 1024 },
  logo: { maxWidth: 400, maxHeight: 400, quality: 0.9, skipBelowSize: 30 * 1024 },
  defect: { maxWidth: 1920, maxHeight: 1920, quality: 0.8, skipBelowSize: 100 * 1024 },
} as const;

/**
 * Check if browser supports WebP encoding
 */
function supportsWebP(): boolean {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL("image/webp").startsWith("data:image/webp");
  } catch {
    return false;
  }
}

/**
 * Load an image file into an HTMLImageElement
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Calculate new dimensions while maintaining aspect ratio
 */
function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } {
  let width = originalWidth;
  let height = originalHeight;

  // Only resize if image is larger than max dimensions
  if (width > maxWidth || height > maxHeight) {
    const aspectRatio = width / height;

    if (width > height) {
      width = Math.min(width, maxWidth);
      height = width / aspectRatio;
    } else {
      height = Math.min(height, maxHeight);
      width = height * aspectRatio;
    }

    // Ensure neither dimension exceeds max
    if (width > maxWidth) {
      width = maxWidth;
      height = width / aspectRatio;
    }
    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }
  }

  return { width: Math.round(width), height: Math.round(height) };
}

/**
 * Compress and optionally convert an image file to WebP
 *
 * Features:
 * - Resizes to max dimensions while maintaining aspect ratio
 * - Converts to WebP (with JPEG fallback)
 * - Skips small files and SVGs
 * - Falls back to original on error
 *
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Compressed file (or original if compression fails/skipped)
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {},
): Promise<{ file: File; wasCompressed: boolean; originalSize: number; compressedSize: number }> {
  const {
    maxWidth = 1920,
    maxHeight = 1920,
    quality = 0.8,
    skipBelowSize = 100 * 1024, // 100KB default
  } = options;

  const originalSize = file.size;

  // Skip SVG files (already vector/optimized)
  if (file.type === "image/svg+xml") {
    return { file, wasCompressed: false, originalSize, compressedSize: originalSize };
  }

  // Skip small files (compression overhead not worth it)
  if (file.size < skipBelowSize) {
    return { file, wasCompressed: false, originalSize, compressedSize: originalSize };
  }

  try {
    // Load image
    const img = await loadImage(file);

    // Calculate new dimensions
    const { width, height } = calculateDimensions(
      img.naturalWidth,
      img.naturalHeight,
      maxWidth,
      maxHeight,
    );

    // Create canvas and draw resized image
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Could not get canvas context");
    }

    // Use high quality image rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, width, height);

    // Clean up object URL
    URL.revokeObjectURL(img.src);

    // Determine output format
    const useWebP = supportsWebP();
    const mimeType = useWebP ? "image/webp" : "image/jpeg";
    const extension = useWebP ? "webp" : "jpg";

    // Convert canvas to blob
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => {
          if (b) resolve(b);
          else reject(new Error("Failed to create blob"));
        },
        mimeType,
        quality,
      );
    });

    // If compressed file is larger than original, return original
    if (blob.size >= file.size) {
      return { file, wasCompressed: false, originalSize, compressedSize: originalSize };
    }

    // Create new file with appropriate name
    const baseName = file.name.replace(/\.[^/.]+$/, "");
    const newFileName = `${baseName}.${extension}`;

    const compressedFile = new File([blob], newFileName, {
      type: mimeType,
      lastModified: Date.now(),
    });

    return {
      file: compressedFile,
      wasCompressed: true,
      originalSize,
      compressedSize: compressedFile.size,
    };
  } catch (error) {
    // On any error, return original file
    console.warn("Image compression failed, using original:", error);
    return { file, wasCompressed: false, originalSize, compressedSize: originalSize };
  }
}

/**
 * Format bytes to human readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
